import { Box, Button, CircularProgress, Dialog, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsIcon } from "@foodpilot/foods";

import type { ProductLineDeleteImpact } from "src/api/client-api/productLines/useDeleteProductLine";

/**
 * The deletion flow has two steps so the user is never surprised:
 *   - "confirm" : initial "are you sure?" shown on every delete click.
 *   - "in-use"  : escalated when the backend returned a 409 because the
 *                 line still has sub-lines and/or products. Confirming this
 *                 step retries with `?force=true`, which cascades the
 *                 deletion (drops descendants + detaches products).
 */
export type DeleteProductLineStep = "confirm" | "in-use";

type Props = {
    open: boolean;
    step: DeleteProductLineStep;
    productLineName: string;
    impact: ProductLineDeleteImpact;
    isDeleting: boolean;
    onClose: () => void;
    onConfirm: () => void;
};

export const DeleteProductLineDialog = (props: Props) => {
    const { open, step, productLineName, impact, isDeleting, onClose, onConfirm } = props;
    const { t } = useTranslation();

    const isInUse = step === "in-use";
    const { descendantsCount, directProductsCount, totalProductsCount } = impact;
    const productsInDescendants = Math.max(0, totalProductsCount - directProductsCount);

    const title =
        isInUse ?
            t("productLine.deleteDialog.inUseTitle", "Product line in use")
        :   t("productLine.deleteDialog.title", "Confirm Delete");

    const subtitle =
        isInUse ?
            t(
                "productLine.deleteDialog.inUseSubtitle",
                "Deleting will remove every sub-line and detach the linked products.",
            )
        :   t("productLine.deleteDialog.subtitle", "This action cannot be undone.");

    const confirmLabel =
        isInUse ?
            t("productLine.deleteDialog.deleteAnyway", "Delete anyway")
        :   t("productLine.deleteDialog.delete", "Delete");

    return (
        <Dialog
            open={open}
            onClose={isDeleting ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {title}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {subtitle}
                    </Typography>
                </Box>
                <Box
                    onClick={isDeleting ? undefined : onClose}
                    sx={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: isDeleting ? "not-allowed" : "pointer",
                        borderRadius: "50%",
                        opacity: isDeleting ? 0.4 : 1,
                        "&:hover": isDeleting ? undefined : { backgroundColor: "#F5F5F5" },
                    }}
                >
                    <FoodsIcon icon="close" size={1} />
                </Box>
            </Box>

            <Box sx={{ padding: "32px", backgroundColor: "#FEFCFB" }}>
                {isInUse ?
                    <Stack spacing={2}>
                        <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                            {t(
                                "productLine.deleteDialog.inUseIntro",
                                'The product line "{{name}}" cannot be deleted as-is because it has the following dependencies:',
                                { name: productLineName },
                            )}
                        </Typography>

                        <Box
                            sx={{
                                backgroundColor: "white",
                                border: "1px solid #E7E6E4",
                                borderRadius: "8px",
                                padding: "16px",
                            }}
                        >
                            <Stack spacing={1}>
                                {descendantsCount > 0 && (
                                    <ImpactRow
                                        label={t(
                                            "productLine.deleteDialog.subLinesImpact",
                                            "{{count}} sub-line(s) will be deleted",
                                            { count: descendantsCount },
                                        )}
                                    />
                                )}
                                {directProductsCount > 0 && (
                                    <ImpactRow
                                        label={t(
                                            "productLine.deleteDialog.directProductsImpact",
                                            "{{count}} product(s) directly attached will be detached",
                                            { count: directProductsCount },
                                        )}
                                    />
                                )}
                                {productsInDescendants > 0 && (
                                    <ImpactRow
                                        label={t(
                                            "productLine.deleteDialog.descendantProductsImpact",
                                            "{{count}} additional product(s) attached to sub-lines will be detached",
                                            { count: productsInDescendants },
                                        )}
                                    />
                                )}
                            </Stack>
                        </Box>

                        <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                            {t(
                                "productLine.deleteDialog.inUseOutro",
                                "Detached products keep all their data; they simply lose their product-line assignment.",
                            )}
                        </Typography>
                    </Stack>
                :   <Typography
                        variant="small-medium"
                        sx={{ lineHeight: "1.6em", color: "#393A36", textAlign: "center" }}
                    >
                        {t(
                            "productLine.deleteDialog.confirmBody",
                            'Are you sure you want to delete the product line "{{name}}"? This action cannot be undone.',
                            { name: productLineName },
                        )}
                    </Typography>
                }
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "12px",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={isDeleting}
                    sx={{
                        minWidth: "100px",
                        height: "40px",
                        padding: "0 16px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        backgroundColor: "transparent",
                        color: "#393A36",
                        border: "1px solid #E7E6E4",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    {t("productLine.deleteDialog.cancel", "Cancel")}
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={isDeleting}
                    // Destructive styling: solid red because the cascade is irreversible
                    // and may detach a large number of products. We deliberately stand
                    // out from the primary green action button used elsewhere.
                    sx={{
                        minWidth: "120px",
                        height: "40px",
                        padding: "0 16px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        background: "linear-gradient(180deg, #E5484D 0%, #C62828 100%)",
                        color: "white !important",
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.1)",
                        "&:hover": {
                            background: "linear-gradient(180deg, #C62828 0%, #B71C1C 100%)",
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.2)",
                        },
                        "&:disabled": {
                            background: "linear-gradient(180deg, #E5484D 0%, #C62828 100%)",
                            color: "white !important",
                            opacity: 0.36,
                        },
                    }}
                >
                    {isDeleting ?
                        <CircularProgress size={20} color="inherit" />
                    :   confirmLabel}
                </Button>
            </Box>
        </Dialog>
    );
};

const ImpactRow = ({ label }: { label: string }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box
            sx={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: "#E5484D",
                flexShrink: 0,
            }}
        />
        <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
            {label}
        </Typography>
    </Box>
);
